import { z } from 'zod';
import {
  isRomanianPublicHoliday,
  ROMANIA_FRAME,
  ROMANIA_TIME_ZONE,
  romaniaDateKey,
  romaniaInstant,
  SCHEDULER_MAX_STAGE_HOURS,
  schedulerEffectiveStartLagMinutes,
  schedulerMinStartLagMinutes,
  schedulerDependencyIndex,
  schedulerParallelGateIndex,
  schedulerParallelGateOffsetMinutes,
  schedulerPredecessorIndex,
  schedulerPriorityLevelEnum,
  schedulerStageCodeEnum,
  schedulerStageCodes,
  schedulerStageForTenderOperation,
  tenderOperationCodes,
  WORK_MINUTES_PER_DAY,
  type ProjectTenderResponse,
  type ReopenSchedulerProjectRequest,
  type SchedulerCalendarDto,
  type SchedulerStageDto,
  type SchedulerStageCode,
  type SchedulerStageDependencies,
  type SchedulerStageDesignatedTeams,
  type SchedulerStageEstimates,
  type SchedulerTeamDto,
  type TimeFrame,
  UTC_FRAME,
} from '@kreart/shared';

/**
 * Shared bits for the enroll / edit scheduling dialogs: hour inputs are kept
 * as strings in the form (house style for numeric fields) and converted to
 * numbers at the API edge.
 */

const hourField = z
  .string()
  .trim()
  .min(1)
  .refine(
    (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= 2000;
    },
    { message: 'errors.invalidNumber' },
  );

/** A start lag: empty (no lag) or a number of hours, same bounds as an estimate. */
const lagField = z
  .string()
  .trim()
  .refine(
    (value) => {
      if (value === '') return true;
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= 2000;
    },
    { message: 'errors.invalidNumber' },
  );

/**
 * A stage's lag field: the workshop floor on top of the bounds (Cantuire and
 * CNC may start no sooner than 2h after the previous operation started). Empty
 * stays allowed everywhere — "after the previous one finishes" is stricter.
 */
function lagFieldFor(code: SchedulerStageCode) {
  const floor = schedulerMinStartLagMinutes[code];
  if (floor === undefined) return lagField;
  return lagField.refine((value) => value === '' || Number(value) * 60 >= floor, {
    message: 'operationsScheduler.enrollDialog.startLagMin',
  });
}

export const schedulingFormSchema = z.object({
  projectId: z.string().min(1),
  importance: schedulerPriorityLevelEnum,
  urgency: schedulerPriorityLevelEnum,
  /** The operation the project actually begins at; earlier ones are skipped. */
  startStageCode: schedulerStageCodeEnum,
  // Built from the code tuple, never hand-listed. The form renders a field per
  // schedulerStageCodes entry, so a stage missing from this object would render
  // a visible input whose value the resolver silently strips — the estimate then
  // arrives as Number(undefined), i.e. NaN.
  hours: z.object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, hourField])) as Record<
      SchedulerStageCode,
      typeof hourField
    >,
  ),
  /**
   * Of each operation, how much is already done but was never clocked — work
   * that arrived part-finished. `hours` above is what is still LEFT to do, so
   * the operation's full estimate is the two added together.
   */
  workedHours: z.object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, hourField])) as Record<
      SchedulerStageCode,
      typeof hourField
    >,
  ),
  /**
   * Which operations this project does NOT undergo, as a checkbox per stage.
   *
   * Held as a full record rather than the API's array so each checkbox binds to
   * its own field — react-hook-form cannot register a checkbox against one
   * position of an array without an index that shifts as items are added and
   * removed. `excludedStageCodes()` converts it at the API edge.
   */
  excluded: z.object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, z.boolean()])) as Record<
      SchedulerStageCode,
      z.ZodBoolean
    >,
  ),
  /**
   * Manual estimated start, held as a `YYYY-MM-DD` date-input string ('' =
   * unset). A planning input only — it never affects the engine; the estimated
   * finish shown in the UI is derived from it. Optional so the enroll and edit
   * dialogs can share this schema.
   */
  plannedStartAt: z.string().optional(),
  /**
   * A warranty intervention's own deadline, as a `YYYY-MM-DD` date-input string
   * ('' = none). Only the edit dialog of an intervention shows it: a production
   * project's deadline belongs to the project itself, and the API refuses the
   * field there. Optional for the same reason `plannedStartAt` is.
   */
  interventionDeadline: z.string().optional(),
  /**
   * How many hours after the PREVIOUS operation started each one may start —
   * '' for the ordinary case, "after the previous one finishes". Kept as
   * strings like every other hour field here, and a record keyed by stage code
   * like `hours`, so each input binds to its own field.
   */
  startLag: z.object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, lagFieldFor(code)])) as Record<
      SchedulerStageCode,
      ReturnType<typeof lagFieldFor>
    >,
  ),
  /**
   * The crews each operation is reserved for, in the order they were picked —
   * [] for the ordinary case, "the scheduler decides". One list per operation
   * for both shifts: each shift's picker shows and edits the crews of its own
   * shift (`teamShift`) and leaves the others alone. A record keyed by stage
   * code like `hours`, so each operation binds to its own field.
   */
  designatedTeams: z.object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, z.array(z.string())])) as Record<
      SchedulerStageCode,
      z.ZodArray<z.ZodString>
    >,
  ),
  /**
   * The operation each one's lag counts from, picked under its lag field —
   * '' for the workshop rule, the previous operation. Kept as typed while the
   * pick does not apply (the operation it names is left out, or skipped by
   * the start): the pipeline then falls back to the rule, see
   * `dependencyChoice`.
   */
  dependsOn: z.object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, z.string()])) as Record<
      SchedulerStageCode,
      z.ZodString
    >,
  ),
});
export type SchedulingFormValues = z.infer<typeof schedulingFormSchema>;

/**
 * The "Redeschide" dialog: which operations a warranty intervention redoes, and
 * how it is planned.
 *
 * Unlike enrollment, nothing is pre-filled — an intervention redoes a PART of
 * the job, and hours copied from the production run would be a confident guess
 * about work nobody has looked at yet. So an operation is chosen by ticking it,
 * and only a ticked one needs (and is sent with) an estimate. The checkbox and
 * hour records are keyed by stage code for the same binding reason as
 * `excluded` above.
 *
 * Messages are i18n keys: TextField translates a dotted message, and the dialog
 * translates the `include` one it renders under the operations' legend.
 */
export const reopenFormSchema = z
  .object({
    importance: schedulerPriorityLevelEnum,
    urgency: schedulerPriorityLevelEnum,
    /** `YYYY-MM-DD` or ''; the intervention's own, never the project's. */
    deadline: z.string(),
    /** `YYYY-MM-DD` or ''. The warranty's end bounds it, checked on submit. */
    plannedStartAt: z.string(),
    notes: z.string().max(2000),
    include: z.object(
      Object.fromEntries(schedulerStageCodes.map((code) => [code, z.boolean()])) as Record<
        SchedulerStageCode,
        z.ZodBoolean
      >,
    ),
    hours: z.object(
      Object.fromEntries(schedulerStageCodes.map((code) => [code, z.string()])) as Record<
        SchedulerStageCode,
        z.ZodString
      >,
    ),
  })
  .superRefine((values, ctx) => {
    const chosen = schedulerStageCodes.filter((code) => values.include[code]);
    if (chosen.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['include'],
        message: 'operationsScheduler.reopenDialog.errors.noStage',
      });
    }
    for (const code of chosen) {
      const raw = values.hours[code];
      const hours = Number(raw);
      // Rounded to whole minutes the way the API stores it: 0.001 h passes a
      // plain "> 0" check yet lands as a 0-minute operation, which the server
      // refuses — so the form refuses it first.
      if (
        raw.trim() === '' ||
        !Number.isFinite(hours) ||
        Math.round(hours * 60) <= 0 ||
        hours > SCHEDULER_MAX_STAGE_HOURS
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['hours', code],
          message: 'operationsScheduler.reopenDialog.errors.hoursRequired',
        });
      }
    }
  });
export type ReopenFormValues = z.infer<typeof reopenFormSchema>;

/** Nothing chosen and nothing guessed: see {@link reopenFormSchema}. */
export function defaultReopenValues(): ReopenFormValues {
  return {
    importance: 'NORMAL',
    urgency: 'NORMAL',
    deadline: '',
    plannedStartAt: '',
    notes: '',
    include: Object.fromEntries(schedulerStageCodes.map((code) => [code, false])) as Record<
      SchedulerStageCode,
      boolean
    >,
    hours: Object.fromEntries(schedulerStageCodes.map((code) => [code, ''])) as Record<
      SchedulerStageCode,
      string
    >,
  };
}

/**
 * The form as the API's request. Only ticked operations are sent — the API
 * reads a key as "redo this one" — so hours left in a field that was later
 * unticked go nowhere. Empty dates and notes are sent as null, meaning "none".
 */
export function toReopenRequest(values: ReopenFormValues): ReopenSchedulerProjectRequest {
  const notes = values.notes.trim();
  return {
    estimatedHours: Object.fromEntries(
      schedulerStageCodes
        .filter((code) => values.include[code])
        .map((code) => [code, Number(values.hours[code])]),
    ) as Partial<Record<SchedulerStageCode, number>>,
    importance: values.importance,
    urgency: values.urgency,
    deadline: dateInputToIso(values.deadline),
    plannedStartAt: dateInputToIso(values.plannedStartAt),
    notes: notes === '' ? null : notes,
  };
}

/**
 * True when a `YYYY-MM-DD` estimated start falls after the warranty's end
 * instant. The day is read as local midnight — exactly what
 * {@link dateInputToIso} sends — so this agrees with the server's own check to
 * the millisecond: the end day itself is still allowed, the day after is not.
 * '' (no start) is never after anything.
 */
export function startsAfterWarranty(plannedStartInput: string, warrantyEndsAt: string): boolean {
  const start = dateInputToIso(plannedStartInput);
  if (start === null) return false;
  return new Date(start).getTime() > new Date(warrantyEndsAt).getTime();
}

/** ISO datetime → `YYYY-MM-DD` for a date input: its date in Romania; '' when unset. */
export function isoToDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return '';
  return romaniaDateKey(ms);
}

/** `YYYY-MM-DD` date input → ISO datetime at midnight in Romania; null when empty. */
export function dateInputToIso(value: string): string | null {
  if (!value) return null;
  const ms = romaniaInstant(value, 0);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toISOString();
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
// The engine's original work window: 08:00–15:00 UTC, i.e. WORK_MINUTES_PER_DAY (420).
const WORK_DAY_START_MS = 8 * 60 * MINUTE_MS;
const WORK_DAY_END_MS = WORK_DAY_START_MS + WORK_MINUTES_PER_DAY * MINUTE_MS;
/** Loop guard, as the API's (engine/work-calendar.ts, MAX_ADVANCE_DAYS). */
const MAX_DAYS = 20_000;

/**
 * The shop's calendar as the planner lays dates out on — the board's
 * `calendar`, once the shop saved one (`plannerCalendarOf`): its program in
 * Romania's own time, summer time included, with the legal holidays off unless
 * the shop opened them — the API's calendar exactly. Absent, the planner keeps
 * its original calendar: weekdays, 08:00–15:00 UTC.
 */
export type PlannerCalendar = Pick<
  SchedulerCalendarDto,
  'startMinutes' | 'endMinutes' | 'weekendsOff' | 'nonWorkingDates' | 'workingDates'
>;

/** The board's calendar for the planner, or nothing while the shop has not saved one. */
export function plannerCalendarOf(
  calendar: SchedulerCalendarDto | null | undefined,
): PlannerCalendar | undefined {
  return calendar?.followWorkerSchedules ? calendar : undefined;
}

/** The frame a calendar's days and hours are read in: Romania's for the shop's, UTC for none. */
function frameOf(calendar: PlannerCalendar | undefined): TimeFrame {
  return calendar ? ROMANIA_FRAME : UTC_FRAME;
}

/** Saturday or Sunday, for the day starting at `day` in `frame`. */
function isWeekend(day: number, frame: TimeFrame): boolean {
  const dow = frame.weekday(day);
  return dow === 0 || dow === 6;
}

/**
 * The work window of the day starting at `day`, or null on a day off. With no
 * calendar: the planner's original one, weekdays 08:00–15:00 UTC. With one: the
 * general program in Romania's time, mirroring the API's `generalCalendar` (an
 * opened date first, then closed dates and the legal holidays, then the
 * weekend).
 */
function windowOn(day: number, calendar: PlannerCalendar | undefined): [number, number] | null {
  if (!calendar) {
    return isWeekend(day, UTC_FRAME) ? null : [day + WORK_DAY_START_MS, day + WORK_DAY_END_MS];
  }
  const frame = ROMANIA_FRAME;
  const key = frame.dateKey(day);
  const open = calendar.workingDates.includes(key)
    ? true
    : calendar.nonWorkingDates.includes(key) || isRomanianPublicHoliday(key)
      ? false
      : !(calendar.weekendsOff && isWeekend(day, frame));
  return open ? [frame.at(day, calendar.startMinutes), frame.at(day, calendar.endMinutes)] : null;
}

/** Roll `ms` forward to the first working instant of the calendar. */
function nextWorkInstant(ms: number, calendar?: PlannerCalendar): number {
  // Both this and addWorkMinutesUtc below loop over arithmetic that silently
  // yields NaN, and every branch is a no-op under NaN — so a non-finite input
  // would not return a wrong date, it would hang the main thread. The API's
  // mirror guards the same way (engine/work-calendar.ts, MAX_ADVANCE_DAYS).
  if (!Number.isFinite(ms)) throw new Error('nextWorkInstant: non-finite input');
  const frame = frameOf(calendar);
  let day = frame.dayStart(ms);
  for (let guard = 0; guard <= MAX_DAYS; guard += 1, day = frame.nextDay(day)) {
    const window = windowOn(day, calendar);
    if (window && window[1] > ms) return Math.max(window[0], ms);
  }
  throw new Error('nextWorkInstant: no working day reachable');
}

/**
 * A projected START instant as the moment work really begins, for display. The
 * projection quotes a crew freed at the end of a day as starting then — it
 * really starts the next working morning. With no calendar that is the
 * original window, every day: at or after 15:00 UTC reads as 08:00 UTC of the
 * next calendar day, before 08:00 as 08:00 that day — unlike the weekday-only
 * planner around it, which must not be used for projected dates. With the
 * shop's calendar, its next working instant. Null (or an unparsable value)
 * stays null.
 */
export function workStartIso(
  value: string | null | undefined,
  calendar?: PlannerCalendar,
): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  if (calendar) {
    const start = nextWorkInstant(ms, calendar);
    return start === ms ? value : new Date(start).toISOString();
  }
  const day = Math.floor(ms / DAY_MS) * DAY_MS;
  if (ms < day + WORK_DAY_START_MS) return new Date(day + WORK_DAY_START_MS).toISOString();
  if (ms >= day + WORK_DAY_END_MS) {
    return new Date(day + DAY_MS + WORK_DAY_START_MS).toISOString();
  }
  return value;
}

/**
 * Earliest instant reached after `minutes` of work from `fromMs`, over the
 * calendar (the planner's original 7h/day, weekends off, when none is given).
 * Mirrors the API's `addWorkMinutes` so the UI's planning estimate lines up
 * with the shop's own arithmetic.
 */
function addWorkMinutesUtc(fromMs: number, minutes: number, calendar?: PlannerCalendar): number {
  if (!Number.isFinite(fromMs) || !Number.isFinite(minutes)) {
    throw new Error('addWorkMinutesUtc: non-finite input');
  }
  if (minutes <= 0) return nextWorkInstant(fromMs, calendar);
  let remaining = minutes * MINUTE_MS;
  const frame = frameOf(calendar);
  let day = frame.dayStart(fromMs);
  for (let guard = 0; guard <= MAX_DAYS; guard += 1, day = frame.nextDay(day)) {
    const window = windowOn(day, calendar);
    if (!window) continue;
    const start = Math.max(window[0], fromMs);
    if (window[1] <= start) continue;
    const available = window[1] - start;
    if (available >= remaining) return start + remaining;
    remaining -= available;
  }
  throw new Error('addWorkMinutesUtc: no working day reachable');
}

/**
 * `YYYY-MM-DD` → ms at that day's work-window start (08:00 UTC with no
 * calendar; the program's start, in Romania's time, with one).
 */
function startOfWorkDayMs(startInput: string, calendar?: PlannerCalendar): number | null {
  const [year, month, day] = startInput.split('-').map(Number);
  if (!year || !month || !day) return null;
  if (calendar) return romaniaInstant(startInput, calendar.startMinutes);
  return Date.UTC(year, month - 1, day) + WORK_DAY_START_MS;
}

/** An instant → the ISO for its date (in `frame`) at noon UTC (safe for `formatDate`). */
function isoDateNoon(ms: number, frame: TimeFrame = UTC_FRAME): string {
  return `${frame.dateKey(frame.dayStart(ms))}T12:00:00.000Z`;
}

/**
 * Estimated finish DATE for a job that starts on `startInput` (`YYYY-MM-DD`)
 * and needs `totalMinutes` of estimated work, laid out over the engine's
 * working calendar (7h/day, weekends off). Returns an ISO at UTC noon, or null
 * when there is no start or no work.
 *
 * Purely a planning estimate: the working-day span depends only on the estimate,
 * so moving the start shifts the finish by the same span — the gap between start
 * and finish stays constant, which is the intended behaviour.
 *
 * @deprecated Sequential only, and blind to start lags: `totalMinutes` is a flat
 * sum, so two operations that overlap are charged twice over. Lay the stages
 * out with {@link planStageSchedule} and read its `finish` instead. Kept for its
 * tests.
 */
export function estimatedFinishIso(
  startInput: string | null | undefined,
  totalMinutes: number,
): string | null {
  if (!startInput || totalMinutes <= 0) return null;
  const startMs = startOfWorkDayMs(startInput);
  if (startMs === null) return null;
  return isoDateNoon(addWorkMinutesUtc(nextWorkInstant(startMs), totalMinutes));
}

/** One operation placed on the planning calendar: ISO start/finish dates. */
export interface PlannedStage {
  stageCode: SchedulerStageCode;
  start: string;
  finish: string;
}

/**
 * One operation to place. `startLagMinutes` is how long after the operation
 * it depends on started this one may start — the previous entry of the list,
 * or `dependsOnStageCode` when that one is in it — see {@link layStagesOut}.
 */
export interface PlannableStage {
  stageCode: SchedulerStageCode;
  minutes: number;
  startLagMinutes?: number | null;
  dependsOnStageCode?: SchedulerStageCode | null;
}

/**
 * Lay the given operations out from `startInput`, each consuming its `minutes`
 * of estimated work over the engine's working calendar: end-to-end, except that
 * an operation with a start lag begins that long after the one it depends on
 * began (`schedulerDependencyIndex`). Operations with no
 * remaining work are skipped. Returns the per-stage dates and the final finish
 * — all derived from the estimated start, so changing it moves them all.
 */
export function planStageSchedule(
  startInput: string | null | undefined,
  stages: PlannableStage[],
  calendar?: PlannerCalendar,
): { stages: PlannedStage[]; finish: string | null } {
  const frame = frameOf(calendar);
  return layStagesOut(startInput, stages, (ms) => isoDateNoon(ms, frame), calendar);
}

/**
 * Like {@link planStageSchedule}, but the per-stage dates keep their exact
 * working-calendar instant (08:00 start, 15:00 finish) instead of snapping to
 * noon. The Gantt draws these on an hour axis where a sub-day operation snapped
 * to noon would collapse to zero width, so it needs the real times.
 */
export function planStageScheduleInstants(
  startInput: string | null | undefined,
  stages: PlannableStage[],
  calendar?: PlannerCalendar,
): { stages: PlannedStage[]; finish: string | null } {
  return layStagesOut(startInput, stages, (ms) => new Date(ms).toISOString(), calendar);
}

/**
 * THE RULE, mirroring the engine's `pipelineStageRelease`: an operation starts
 * when the previous one finishes, or — with a start lag — that many work-minutes
 * after the previous one STARTED, whichever is earlier. The cap means a lag can
 * only ever make two operations overlap; it never opens a gap. The workshop
 * rules apply on top (see `schedulerPredecessorIndex` in @kreart/shared):
 * Cantuire and CNC lag at least 2h, Vopsire runs beside Asamblare, and Montaj
 * starts no sooner than Vopsire having half of Montaj left to run.
 *
 * "Previous" is read off the list this was handed, after dropping
 * operations with no work left. Callers filter the list differently (the Gantt
 * keeps DONE operations, the project row drops them, the enroll dialog drops
 * everything before the start point), and each gets an answer consistent with
 * the list it actually passed.
 *
 * This lane is deliberately capacity-blind: it knows nothing about crews, so a
 * lag always draws as an overlap. The engine may still run the two end to end
 * when one crew covers both operations — an expected divergence between the
 * estimated plan and the dispatcher's.
 */
function layStagesOut(
  startInput: string | null | undefined,
  stages: PlannableStage[],
  format: (ms: number) => string,
  calendar: PlannerCalendar | undefined,
): { stages: PlannedStage[]; finish: string | null } {
  if (!startInput) return { stages: [], finish: null };
  const startMs = startOfWorkDayMs(startInput, calendar);
  if (startMs === null) return { stages: [], finish: null };
  // Tracked in MILLISECONDS, before `format`: `planStageSchedule` formats
  // through `isoDateNoon`, which would flatten two same-day finishes — and a
  // previous start that must be counted from 08:00, not noon — into strings.
  const active = stages.filter((stage) => stage.minutes > 0);
  const codes = active.map((stage) => stage.stageCode);
  const starts: number[] = [];
  const finishes: number[] = [];
  let maxFinishMs: number | null = null;
  const placed: PlannedStage[] = [];
  for (const [index, stage] of active.entries()) {
    let from = startMs;
    const previous = schedulerDependencyIndex(codes, index, stage.dependsOnStageCode);
    if (previous !== null) {
      from = finishes[previous]!;
      const lag = schedulerEffectiveStartLagMinutes(stage.stageCode, stage.startLagMinutes ?? null);
      if (lag !== null) {
        from = Math.min(addWorkMinutesUtc(starts[previous]!, lag, calendar), finishes[previous]!);
      }
    }
    const painting = schedulerParallelGateIndex(codes, index, stage.dependsOnStageCode);
    if (painting !== null) {
      const gate = addWorkMinutesUtc(
        starts[painting]!,
        schedulerParallelGateOffsetMinutes(active[painting]!.minutes, stage.minutes),
        calendar,
      );
      if (gate > from) from = gate;
    }
    const stageStart = nextWorkInstant(from, calendar);
    const stageFinish = addWorkMinutesUtc(stageStart, stage.minutes, calendar);
    placed.push({
      stageCode: stage.stageCode,
      start: format(stageStart),
      finish: format(stageFinish),
    });
    // The LAST finish, not the last operation placed: with a lag a short
    // operation can end before the longer one it follows.
    if (maxFinishMs === null || stageFinish > maxFinishMs) maxFinishMs = stageFinish;
    starts.push(stageStart);
    finishes.push(stageFinish);
  }
  return { stages: placed, finish: maxFinishMs === null ? null : format(maxFinishMs) };
}

/**
 * Estimated work still ahead of a project, in minutes: the sum over every stage
 * that is neither excluded nor already DONE of `estimatedMinutes − workedMinutes`
 * (never negative).
 *
 * @deprecated Sequential only. A flat sum is a LABOUR figure, not a calendar
 * one: operations that overlap through a start lag need less of the working
 * calendar than their labour adds up to. Use {@link planStageSchedule}, which lays them out properly. Kept
 * for its tests.
 */
export function remainingEstimatedMinutes(
  stages: Pick<SchedulerStageDto, 'estimatedMinutes' | 'workedMinutes' | 'status' | 'excludedAt'>[],
): number {
  return stages.reduce((total, stage) => {
    if (stage.excludedAt || stage.status === 'DONE') return total;
    return total + Math.max(0, stage.estimatedMinutes - stage.workedMinutes);
  }, 0);
}

/**
 * The API's total estimate per operation. The "hours" field already carries the
 * operation's whole estimate (autofilled from the deviz distribution), and the
 * API stores it as the total while subtracting `workedMinutes` to schedule only
 * what is left. So the estimate must be sent as-is — adding the already-done
 * hours on top would double-count them, inflating the board total and its
 * progress bar (e.g. "10h of 49.5h" instead of "10h of 39.5h").
 */
export function hoursToEstimates(hours: SchedulingFormValues['hours']): SchedulerStageEstimates {
  return Object.fromEntries(
    schedulerStageCodes.map((code) => [code, Number(hours[code])]),
  ) as SchedulerStageEstimates;
}

/** The already-done half of the split, as the API's partial map. */
export function hoursToWorked(
  workedHours: SchedulingFormValues['workedHours'],
): SchedulerStageEstimates {
  return Object.fromEntries(
    schedulerStageCodes.map((code) => [code, Number(workedHours[code])]),
  ) as SchedulerStageEstimates;
}

/** Nothing done yet: the ordinary case, a project starting from scratch. */
export function defaultWorkedHours(): SchedulingFormValues['workedHours'] {
  return Object.fromEntries(schedulerStageCodes.map((code) => [code, '0'])) as Record<
    SchedulerStageCode,
    string
  >;
}

export function minutesToHoursString(minutes: number): string {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : String(Math.round(hours * 100) / 100);
}

export function defaultHours(): SchedulingFormValues['hours'] {
  return Object.fromEntries(schedulerStageCodes.map((code) => [code, '7'])) as Record<
    SchedulerStageCode,
    string
  >;
}

/** Nothing excluded: the default assumption is that a project is fully built. */
export function defaultExcluded(): SchedulingFormValues['excluded'] {
  return Object.fromEntries(schedulerStageCodes.map((code) => [code, false])) as Record<
    SchedulerStageCode,
    boolean
  >;
}

/** The form's checkbox record as the API's array. */
export function toExcludedStageCodes(
  excluded: SchedulingFormValues['excluded'],
): SchedulerStageCode[] {
  return schedulerStageCodes.filter((code) => excluded[code]);
}

/**
 * The workshop floors (Cantuire and CNC: 2h after the previous operation
 * started); every other operation waits for the one before it to finish.
 */
export function defaultStartLag(): SchedulingFormValues['startLag'] {
  return Object.fromEntries(
    schedulerStageCodes.map((code) => {
      const floor = schedulerMinStartLagMinutes[code];
      return [code, floor === undefined ? '' : minutesToHoursString(floor)];
    }),
  ) as Record<SchedulerStageCode, string>;
}

/** Nobody reserved: the scheduler picks the crew, which is how it has always worked. */
export function defaultDesignatedTeams(): SchedulingFormValues['designatedTeams'] {
  return Object.fromEntries(schedulerStageCodes.map((code) => [code, []])) as unknown as Record<
    SchedulerStageCode,
    string[]
  >;
}

/** A project's stored reservations as the form record, for the edit dialog. */
export function designatedTeamsFromStages(
  stages: Array<{ stageCode: string; designatedTeams: Array<{ id: string }> }>,
): SchedulingFormValues['designatedTeams'] {
  const byCode = new Map(
    stages.map((stage) => [stage.stageCode, stage.designatedTeams.map((team) => team.id)]),
  );
  return Object.fromEntries(
    schedulerStageCodes.map((code) => [code, byCode.get(code) ?? []]),
  ) as unknown as Record<SchedulerStageCode, string[]>;
}

/**
 * The form record as the API's map, for enrolment: the operations that have
 * crews named, in the order they were picked. One skipped by the start or
 * ticked off keeps them — planned for the day it is put back, as "Editează
 * programarea" allows too.
 */
export function toDesignatedTeamIds(
  designatedTeams: SchedulingFormValues['designatedTeams'],
): SchedulerStageDesignatedTeams {
  return Object.fromEntries(
    schedulerStageCodes.flatMap((code): [SchedulerStageCode, string[]][] =>
      designatedTeams[code].length === 0 ? [] : [[code, [...designatedTeams[code]]]],
    ),
  ) as SchedulerStageDesignatedTeams;
}

/** The shift a crew works — 2 for a second-shift crew, else 1 (what an older board says). */
export function teamShift(team: Pick<SchedulerTeamDto, 'shift'>): 1 | 2 {
  return team.shift ?? 1;
}

/**
 * The crews offerable for one operation, best first: the active ones that
 * perform it, home crews before merely cross-skilled ones — the same order, and
 * the same reasoning, the server's own manual picker uses.
 *
 * COLLABORATOR crews are in: they are external, but the scheduler treats them
 * as capacity like any other, and reserving a job for a particular subcontractor
 * is exactly the kind of thing this field is for.
 */
export function teamsForStage(
  teams: SchedulerTeamDto[],
  code: SchedulerStageCode,
): SchedulerTeamDto[] {
  return teams
    .filter(
      (team) => team.active && (team.stageCode === code || team.extraStageCodes.includes(code)),
    )
    .sort(
      (a, b) =>
        Number(a.stageCode !== code) - Number(b.stageCode !== code) ||
        a.name.localeCompare(b.name, 'ro', { numeric: true }),
    );
}

/** A project's stored lags (minutes) as the form record (hours), for the edit dialog. */
export function startLagFromStages(
  stages: Array<{ stageCode: string; startLagMinutes: number | null }>,
): SchedulingFormValues['startLag'] {
  const lags = new Map(stages.map((stage) => [stage.stageCode, stage.startLagMinutes]));
  return Object.fromEntries(
    schedulerStageCodes.map((code) => {
      const minutes = lags.get(code);
      return [code, minutes === null || minutes === undefined ? '' : minutesToHoursString(minutes)];
    }),
  ) as Record<SchedulerStageCode, string>;
}

/** One form value as hours for the API: null for an empty field. */
export function lagHours(value: string | undefined): number | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * The form record as the API's partial map: only operations that have a lag
 * field in the pipeline the form describes. A value left in a field that is no
 * longer shown — the operation was excluded, or became the first one — is not
 * sent, so it cannot resurface later as a lag nobody remembers setting.
 *
 * An EMPTY field on an operation with a workshop floor is sent as null: the
 * server would otherwise enrol it with the floor, and the manager asked for
 * "after the previous one finishes".
 */
export function toStartLagHours(
  values: Pick<SchedulingFormValues, 'startLag' | 'excluded' | 'startStageCode'>,
): Partial<Record<SchedulerStageCode, number | null>> {
  return Object.fromEntries(
    schedulerStageCodes.flatMap((code): [SchedulerStageCode, number | null][] => {
      if (!previousActiveStageCode(code, values)) return [];
      const hours = lagHours(values.startLag[code]);
      if (hours === null) {
        return schedulerMinStartLagMinutes[code] === undefined ? [] : [[code, null]];
      }
      return [[code, hours]];
    }),
  ) as Partial<Record<SchedulerStageCode, number | null>>;
}

/**
 * The operation `code`'s start lag counts from in the active pipeline the form
 * describes — the one picked under its lag field when that applies, else its
 * predecessor under the workshop rules, so Asamblare names CNC, not the Vopsire
 * running beside it. Null when `code` heads the pipeline or is not in it: no
 * lag field is shown then.
 */
export function previousActiveStageCode(
  code: SchedulerStageCode,
  values: Pick<SchedulingFormValues, 'excluded' | 'startStageCode'> &
    Partial<Pick<SchedulingFormValues, 'dependsOn'>>,
): SchedulerStageCode | null {
  return dependencyChoice(code, values)?.current ?? null;
}

/**
 * What `code` may depend on in the active pipeline the form describes: the
 * operations before it (`options`, in order), the one the workshop rule picks
 * (`rule`) and the one in force (`current`: the pick when it is among the
 * options, else the rule). Null when there is nothing to pick — `code` heads
 * the pipeline or is not in it.
 */
export function dependencyChoice(
  code: SchedulerStageCode,
  values: Pick<SchedulingFormValues, 'excluded' | 'startStageCode'> &
    Partial<Pick<SchedulingFormValues, 'dependsOn'>>,
): { options: SchedulerStageCode[]; rule: SchedulerStageCode; current: SchedulerStageCode } | null {
  const startIndex = schedulerStageCodes.indexOf(values.startStageCode);
  const active = schedulerStageCodes.filter(
    (candidate, index) => index >= startIndex && !values.excluded?.[candidate],
  );
  const position = active.indexOf(code);
  const rule = position < 0 ? null : schedulerPredecessorIndex(active, position);
  if (rule === null) return null;
  const current = schedulerDependencyIndex(active, position, values.dependsOn?.[code] || null);
  return {
    options: active.slice(0, position),
    rule: active[rule]!,
    current: active[current ?? rule]!,
  };
}

/**
 * Who stops to wait because of a reopened operation, so the dialog can name
 * them. Judged as the server's pipeline sync will, on the pipeline the project
 * has now:
 *
 *   - an operation depending on a reopened one with no start lag waits for it to
 *     finish. With a lag it counts from that one's START instead, so it waits for
 *     no adjustment and is not named — at most for what is left of the lag.
 *     Unless the reopened one never started (closed with hours collected off the
 *     board): then there is no start to count from until it gets one;
 *   - Montaj is also held by a reopened Vopsire (the workshop gate);
 *   - one already running, or waiting in the queue with a start or hours on it,
 *     keeps its place.
 */
export function heldBackByReopening(
  stages: ReadonlyArray<
    Pick<
      SchedulerStageDto,
      | 'stageCode'
      | 'orderIndex'
      | 'status'
      | 'startedAt'
      | 'completedAt'
      | 'workedMinutes'
      | 'actualWorkedMinutes'
    >
  >,
  reopened: readonly SchedulerStageCode[],
  values: Pick<SchedulingFormValues, 'excluded' | 'startLag' | 'dependsOn'>,
): Array<{ code: SchedulerStageCode; on: SchedulerStageCode }> {
  const isReopened = new Set<string>(reopened);
  const pipeline = [...stages]
    .filter((stage) => !(stage.status === 'DONE' && stage.completedAt === null))
    // Ticked off in this same save: gone, unless its hours close it instead —
    // a finished operation is still somebody's predecessor.
    .filter(
      (stage) =>
        !values.excluded[stage.stageCode as SchedulerStageCode] ||
        (stage.actualWorkedMinutes ?? stage.workedMinutes) > 0,
    )
    .sort((a, b) => a.orderIndex - b.orderIndex);
  const codes = pipeline.map((stage) => stage.stageCode);
  return pipeline.flatMap((stage, index) => {
    const code = stage.stageCode as SchedulerStageCode;
    const waiting =
      isReopened.has(code) ||
      stage.status === 'PENDING' ||
      (stage.status === 'QUEUED' && stage.startedAt === null && stage.workedMinutes === 0);
    if (!waiting || values.excluded[code]) return [];
    const dependsOn = values.dependsOn[code] || null;
    const at = schedulerDependencyIndex(codes, index, dependsOn);
    const dependency = at === null ? null : pipeline[at]!;
    if (
      dependency &&
      isReopened.has(dependency.stageCode) &&
      (lagHours(values.startLag[code]) === null || dependency.startedAt === null)
    ) {
      return [{ code, on: dependency.stageCode as SchedulerStageCode }];
    }
    const gate = schedulerParallelGateIndex(codes, index, dependsOn);
    return gate !== null && isReopened.has(codes[gate]!)
      ? [{ code, on: codes[gate] as SchedulerStageCode }]
      : [];
  });
}

/**
 * What stops when "Mută proiectul în etapa respectivă" takes the project back to
 * `target`, so the dialog can name it: the operations after it that run or wait in
 * a queue, unless ticked off in this same save. One finished stays finished, and
 * one still pending has nothing to stop.
 */
export function stoppedByMove(
  stages: ReadonlyArray<
    Pick<SchedulerStageDto, 'stageCode' | 'orderIndex' | 'status' | 'teamName'>
  >,
  target: SchedulerStageCode,
  excluded: Partial<Record<SchedulerStageCode, boolean>> | undefined,
): Array<{ code: SchedulerStageCode; teamName: string | null }> {
  const at = stages.find((stage) => stage.stageCode === target)?.orderIndex;
  if (at === undefined) return [];
  return [...stages]
    .filter(
      (stage) =>
        stage.orderIndex > at &&
        (stage.status === 'IN_PROGRESS' || stage.status === 'QUEUED') &&
        !excluded?.[stage.stageCode as SchedulerStageCode],
    )
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((stage) => ({ code: stage.stageCode as SchedulerStageCode, teamName: stage.teamName }));
}

/** Nothing picked: every operation follows the workshop rule. */
export function defaultDependsOn(): SchedulingFormValues['dependsOn'] {
  return Object.fromEntries(schedulerStageCodes.map((code) => [code, ''])) as Record<
    SchedulerStageCode,
    string
  >;
}

/** A project's stored picks as the form record, for the edit dialog. */
export function dependsOnFromStages(
  stages: Array<{ stageCode: string; dependsOnStageCode?: string | null }>,
): SchedulingFormValues['dependsOn'] {
  const byCode = new Map(stages.map((stage) => [stage.stageCode, stage.dependsOnStageCode]));
  return Object.fromEntries(
    schedulerStageCodes.map((code) => [code, byCode.get(code) ?? '']),
  ) as Record<SchedulerStageCode, string>;
}

/**
 * The picks as the API's map, for enrolment: only the operations whose pick
 * is in force and differs from the rule. One that does not apply in the
 * pipeline being enrolled is not sent, so it cannot resurface later as a
 * dependency nobody remembers picking.
 */
export function toDependsOnStageCodes(
  values: Pick<SchedulingFormValues, 'dependsOn' | 'excluded' | 'startStageCode'>,
): SchedulerStageDependencies {
  return Object.fromEntries(
    schedulerStageCodes.flatMap((code): [SchedulerStageCode, SchedulerStageCode][] => {
      const choice = dependencyChoice(code, values);
      return choice && choice.current !== choice.rule ? [[code, choice.current]] : [];
    }),
  ) as SchedulerStageDependencies;
}

/** A project's stages as the checkbox record, for the edit dialog. */
export function excludedFromStages(
  stages: Array<{ stageCode: string; excludedAt: string | null }>,
): SchedulingFormValues['excluded'] {
  const excluded = new Set(
    stages.filter((stage) => stage.excludedAt !== null).map((stage) => stage.stageCode),
  );
  return Object.fromEntries(
    schedulerStageCodes.map((code) => [code, excluded.has(code)]),
  ) as Record<SchedulerStageCode, boolean>;
}

/** Two decimals at most, and no trailing ".00" — same shape the fields accept. */
function hoursString(hours: number): string {
  return String(Math.round(hours * 100) / 100);
}

/**
 * A project's labour hours as Project Organization left them, ready for the
 * enrollment form.
 *
 * `total` is what the scheduler gates on: zero means the labour budget was
 * never handed out across the operations, and there is nothing to schedule
 * from.
 *
 * EVERY operation is costed now, so every stage takes its estimate from the
 * costing — including DESIGN, which used to keep the form default because the
 * tender priced manufacturing only. The consequence is deliberate but worth
 * knowing: a shop that has not given Proiectare or Vopsitorie a share reserves
 * zero hours for them, so those stages enroll at 0 rather than at the old 7h
 * default. Their hours come from the costing or not at all — inventing a
 * fallback here would schedule work nobody budgeted.
 */
export interface TenderDistribution {
  /** Hours reserved across every costed operation, summed. */
  total: number;
  /** Those hours as form values, one per stage. */
  hours: SchedulingFormValues['hours'];
  /** The labour-hour budget they were meant to divide; null when unavailable. */
  budget: number | null;
}

export function tenderDistribution(tender: ProjectTenderResponse): TenderDistribution {
  const hours = defaultHours();
  let total = 0;
  for (const code of tenderOperationCodes) {
    const reserved = tender.reservedHours[code];
    total += reserved;
    hours[schedulerStageForTenderOperation[code]] = hoursString(reserved);
  }
  return { total: Math.round(total * 100) / 100, hours, budget: tender.budgetLaborHours };
}

/** An instant as the shop reads it: the date and time in Romania. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: ROMANIA_TIME_ZONE,
  }).format(new Date(value));
}

/** An instant's date in Romania. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'medium',
    timeZone: ROMANIA_TIME_ZONE,
  }).format(new Date(value));
}

/** "3h" / "2.5h" / "12m" — compact duration for chips and suggestions. */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  const rounded = Math.round(hours * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}h`;
}
